import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import PropTypes from "prop-types";
import { Row, Table } from "react-bootstrap";
import { startOfYear, startOfDay, format } from "date-fns";

import { ProjectServiceAnalysisFilters } from "./components";

import {
  summaryByProjectAndServiceColumns,
  summaryByProjectColumns,
  listColumns
} from "./ProjectServiceAnalysis.columns";

import { generateUniqueId } from "../../../utils";
import Loading from "../../common/Loading";

import * as reportActions from "../../../actions/reportActions";
import * as exportActions from "../../../actions/exportActions";

const ProjectServiceAnalysis = ({
  actions,
  isMobile,
  loading,
  report,
  exporting,
  exported
}) => {
  const [filters, setFilters] = useState({
    startDate: startOfYear(new Date()),
    endDate: startOfDay(new Date()),
    customer: null,
    project: [],
    callStatus: [],
    printDisplay: 3
  });
  const [selectedColumns, setSelectedColumns] = useState([]);

  useEffect(() => {
    if (filters.customer && filters.printDisplay) {
      actions.getProjectServiceAnalysisReport({
        ...filters,
        project: filters.project.map((p) => p.value),
        callStatus: filters.callStatus.map((p) => p.value)
      });
    } else {
      setSelectedColumns([]);
    }
  }, [actions, filters]);

  useEffect(() => {
    if (filters.printDisplay) {
      if (filters.printDisplay === 1 || filters.printDisplay === 2) {
        setSelectedColumns(listColumns);
      } else if (filters.printDisplay === 3) {
        setSelectedColumns(summaryByProjectAndServiceColumns);
      } else {
        setSelectedColumns(summaryByProjectColumns);
      }
    }
  }, [filters.printDisplay]);

  useEffect(() => {
    if (exported) {
      const url = window.URL.createObjectURL(
        new Blob([exported], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Project-Service-Analysis-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      actions.clearExported();
    }
  }, [exported, actions]);

  const handleFiltersChange = (newFilters) => {
    actions.clearProjectServiceAnalysisReport();
    setFilters(newFilters);
  };

  const renderListOnly = useMemo(
    () =>
      report.map((item) =>
        item.services.map((service) =>
          service.calls
            .sort(
              (a, b) => new Date(a.completed_date) - new Date(b.completed_date)
            )
            .map((call) => (
              <tr key={generateUniqueId()}>
                <td>{call.customerDetails.name}</td>
                <td>{call.projectDetails.name}</td>
                <td>{call.serviceDetails.description}</td>
                <td>{call.stageDetails.label}</td>
                <td>{format(new Date(call.completed_date), "MM/dd/yyyy")}</td>
                <td>{call.userDetails.full_name}</td>
                <td>{call.actual_hrs}</td>
                <td>{call.billable_hrs}</td>
                <td>{call.billing_description}</td>
                <td>{call.internal_notes}</td>
              </tr>
            ))
        )
      ),
    [report]
  );

  const renderListAndSubtotal = useMemo(
    () =>
      report.map((project) => {
        const projectCalls = project.services.map((service) => {
          const serviceCalls = service.calls
            .sort(
              (a, b) => new Date(a.completed_date) - new Date(b.completed_date)
            )
            .map((call) => (
              <tr key={generateUniqueId()}>
                <td>{call.customerDetails.name}</td>
                <td>{call.projectDetails.name}</td>
                <td>{call.serviceDetails.description}</td>
                <td>{call.stageDetails.label}</td>
                <td>{format(new Date(call.completed_date), "MM/dd/yyyy")}</td>
                <td>{call.userDetails.full_name}</td>
                <td>{call.actual_hrs}</td>
                <td>{call.billable_hrs}</td>
                <td>{call.billing_description}</td>
                <td>{call.internal_notes}</td>
              </tr>
            ));
          return (
            <React.Fragment key={generateUniqueId()}>
              {serviceCalls}
              <tr>
                <td />
                <td />
                <td>
                  <b>{service.service.description} Total</b>
                </td>
                <td />
                <td />
                <td />
                <td>
                  <b>{service.totalActual}</b>
                </td>
                <td>
                  <b>{service.totalBillable}</b>
                </td>
                <td />
                <td />
              </tr>
            </React.Fragment>
          );
        });
        return (
          <React.Fragment key={generateUniqueId()}>
            {projectCalls}
            <tr>
              <td />
              <td>
                <b>{project.project.name} Total</b>
              </td>
              <td />
              <td />
              <td />
              <td />
              <td>
                <b>{project.totalActual}</b>
              </td>
              <td>
                <b>{project.totalBillable}</b>
              </td>
              <td />
              <td />
            </tr>
          </React.Fragment>
        );
      }),
    [report]
  );

  const renderSummaryByProjectAndService = useMemo(
    () =>
      report.map((project) => {
        const services = project.services
          .sort((a, b) => {
            const descA = a.service.description.toUpperCase();
            const descB = b.service.description.toUpperCase();
            if (descA < descB) {
              return -1;
            }
            if (descA > descB) {
              return 1;
            }
            return 0;
          })
          .map((service) => (
            <tr key={generateUniqueId()}>
              <td />
              <td>
                <b>{service.service.description} Total</b>
              </td>
              <td>
                <b>{service.totalActual}</b>
              </td>
              <td>
                <b>{service.totalBillable}</b>
              </td>
            </tr>
          ));
        return (
          <React.Fragment key={generateUniqueId()}>
            {services}
            <tr>
              <td>
                <b>{project.project.name} Total</b>
              </td>
              <td />
              <td>
                <b>{project.totalActual}</b>
              </td>
              <td>
                <b>{project.totalBillable}</b>
              </td>
            </tr>
          </React.Fragment>
        );
      }),
    [report]
  );

  const renderSummaryByProject = useMemo(
    () =>
      report.map((project) => (
        <tr key={generateUniqueId()}>
          <td>
            <b>{project.project.name} Total</b>
          </td>
          <td>
            <b>{project.totalActual}</b>
          </td>
          <td>
            <b>{project.totalBillable}</b>
          </td>
        </tr>
      )),
    [report]
  );

  const handleExportPDF = () => {
    const queryFilters = {
      ...filters,
      project: filters.project.length
        ? filters.project.map((p) => p.value)
        : null,
      callStatus: filters.callStatus.length
        ? filters.callStatus.map((p) => p.value)
        : null
    };
    const payload = {
      data: queryFilters,
      type: "PROJECT_SERVICE_ANALYSIS",
      format: "pdf"
    };
    actions.performExport(payload);
  };

  return (
    <div>
      <ProjectServiceAnalysisFilters
        onFiltersChange={handleFiltersChange}
        isMobile={isMobile}
        exportDisabled={loading || exporting || !report.length}
        onPDFExport={handleExportPDF}
      />
      {loading && (
        <Row style={{ display: "flex", justifyContent: "center" }}>
          <Loading />
        </Row>
      )}
      {!loading && report.length ? (
        <div className="content-wrapper" style={{ padding: 0 }}>
          <Table responsive style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                {selectedColumns.map((column) => (
                  <th key={generateUniqueId()}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filters.printDisplay === 1 ? renderListAndSubtotal : null}
              {filters.printDisplay === 2 ? renderListOnly : null}
              {filters.printDisplay === 3
                ? renderSummaryByProjectAndService
                : null}
              {filters.printDisplay === 4 ? renderSummaryByProject : null}
            </tbody>
          </Table>
        </div>
      ) : null}
    </div>
  );
};

ProjectServiceAnalysis.propTypes = {
  actions: PropTypes.object,
  isMobile: PropTypes.bool,
  report: PropTypes.array,
  loading: PropTypes.bool,
  exporting: PropTypes.bool,
  exported: PropTypes.string
};

function mapStatesToProps(state) {
  return {
    report: state.reducers.PSAReport,
    loading: state.reducers.loading,
    codes: state.reducers.codes,
    exporting: state.reducers.exporting,
    exported: state.reducers.exported
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...reportActions,
        ...exportActions
      },
      dispatch
    )
  };
}

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(ProjectServiceAnalysis);
